import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common/UI";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Buildings, PencilSimple, Trash } from "@phosphor-icons/react";

const blankForm = {
  name: "", code: "", address: "", phone: "", email: "",
  registration_no: "", nabh_no: "", mrn_prefix: "MRN",
  opd_module_enabled: true,
};

export default function HospitalsPage() {
  const [list, setList] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState(null); // hospital being edited
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(blankForm);

  const load = () => api.get("/hospitals").then((r) => setList(r.data || []));
  useEffect(() => { load(); }, []);

  const openEdit = (h) => {
    setEditing(h);
    setForm({
      name: h.name || "", code: h.code || "", address: h.address || "",
      phone: h.phone || "", email: h.email || "",
      registration_no: h.registration_no || "", nabh_no: h.nabh_no || "",
      mrn_prefix: h.mrn_prefix || "MRN",
      opd_module_enabled: h.opd_module_enabled !== false,
    });
  };

  const closeAll = () => {
    setCreateOpen(false);
    setEditing(null);
    setForm(blankForm);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        // Code is immutable post-creation
        const payload = { ...form };
        delete payload.code;
        await api.patch(`/hospitals/${editing.id}`, payload);
        toast.success(`Updated "${form.name}"`);
      } else {
        await api.post("/hospitals", form);
        toast.success("Hospital created");
      }
      closeAll();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const del = async (h) => {
    const step1 = window.confirm(
      `Delete hospital "${h.name}" (${h.code})?\n\n` +
      `This will be blocked if the hospital still has staff, patients, admissions or bills. ` +
      `You can choose to wipe everything in the next step.`,
    );
    if (!step1) return;
    try {
      await api.delete(`/hospitals/${h.id}`);
      toast.success(`Deleted "${h.name}"`);
      load();
      return;
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Detail contains the data summary — ask if user wants to cascade.
      const txt = typeof detail === "string" ? detail : formatApiErrorDetail(detail);
      const confirmText = `WIPE ${h.code}`;
      const reply = window.prompt(
        `${txt}\n\n` +
        `Type "${confirmText}" to PERMANENTLY DELETE the hospital and ALL its data:\n` +
        `• Patients  • OPD / IPD records  • Bills & payments\n` +
        `• Discharge summaries  • Staff logins  • Masters\n` +
        `This CANNOT be undone.`,
      );
      if (reply !== confirmText) {
        if (reply !== null) toast.error("Confirmation text did not match — hospital was NOT deleted");
        return;
      }
      try {
        const r = await api.delete(`/hospitals/${h.id}?cascade=true`);
        const d = r.data?.deleted || {};
        toast.success(
          `Wiped "${h.name}": ${d.patients || 0} patients · ${d.admissions || 0} admissions · ` +
          `${d.opd_visits || 0} OPD · ${d.bills || 0} bills · ${d.users || 0} users`,
        );
        load();
      } catch (err2) {
        toast.error(formatApiErrorDetail(err2.response?.data?.detail) || "Cascade delete failed");
      }
    }
  };

  return (
    <div data-testid="hospitals-page">
      <PageHeader
        eyebrow="Superuser"
        title="All Hospitals"
        subtitle="Each hospital is a fully isolated tenant. Edit details inline, or delete a hospital after confirmation."
        actions={
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-hospital-btn" onClick={() => setForm(blankForm)} className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
                <Plus size={14} weight="bold" className="mr-1.5" /> New hospital
              </Button>
            </DialogTrigger>
            <HospitalFormDialog
              title="Provision a new hospital"
              form={form} setForm={setForm}
              busy={busy} onSubmit={submit}
              isEdit={false}
            />
          </Dialog>
        }
      />

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) closeAll(); }}>
        <HospitalFormDialog
          title={`Edit hospital · ${editing?.code || ""}`}
          form={form} setForm={setForm}
          busy={busy} onSubmit={submit}
          isEdit={true}
        />
      </Dialog>

      {list.length === 0 ? (
        <EmptyState title="No hospitals yet" hint="Create your first hospital to begin." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {list.map((h) => (
            <div key={h.id} className="hms-card p-5 relative" data-testid={`hospital-card-${h.code}`}>
              <div className="flex items-start gap-3 mb-3">
                <div className="h-10 w-10 grid place-items-center bg-[var(--hms-paper)] border border-[var(--hms-border-2)] rounded-sm">
                  <Buildings size={20} weight="bold" className="text-[var(--hms-ink)]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold truncate">{h.name}</div>
                  <div className="font-mono text-[11px] text-[var(--hms-muted)]">{h.code} · {h.mrn_prefix}-XXXXXX</div>
                </div>
                <div className="flex flex-col gap-1">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(h)} data-testid={`hospital-edit-${h.code}`} className="rounded-sm text-[var(--hms-text-2)] hover:text-[var(--hms-ink)] h-7 px-2" title="Edit hospital">
                    <PencilSimple size={14} weight="bold" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => del(h)} data-testid={`hospital-del-${h.code}`} className="rounded-sm text-[var(--hms-accent)] h-7 px-2" title="Delete hospital">
                    <Trash size={14} weight="bold" />
                  </Button>
                </div>
              </div>
              <dl className="grid grid-cols-2 gap-2 text-xs">
                <div><dt className="text-[var(--hms-muted)] uppercase text-[10px] tracking-wider">Reg.</dt><dd>{h.registration_no || "—"}</dd></div>
                <div><dt className="text-[var(--hms-muted)] uppercase text-[10px] tracking-wider">NABH</dt><dd>{h.nabh_no || "—"}</dd></div>
                <div className="col-span-2"><dt className="text-[var(--hms-muted)] uppercase text-[10px] tracking-wider">Address</dt><dd>{h.address || "—"}</dd></div>
                <div><dt className="text-[var(--hms-muted)] uppercase text-[10px] tracking-wider">Phone</dt><dd>{h.phone || "—"}</dd></div>
                <div><dt className="text-[var(--hms-muted)] uppercase text-[10px] tracking-wider">Email</dt><dd className="truncate">{h.email || "—"}</dd></div>
              </dl>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HospitalFormDialog({ title, form, setForm, busy, onSubmit, isEdit }) {
  return (
    <DialogContent className="rounded-sm">
      <DialogHeader>
        <DialogTitle className="font-heading">{title}</DialogTitle>
      </DialogHeader>
      <form onSubmit={onSubmit} className="grid grid-cols-2 gap-3" data-testid={isEdit ? "edit-hospital-form" : "new-hospital-form"}>
        <Field label="Name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="h-name" colSpan={2} />
        <Field label="Code" required value={form.code} onChange={(v) => setForm({ ...form, code: v.toUpperCase() })} testid="h-code" disabled={isEdit} />
        <div>
          <label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">MRN Prefix</label>
          <input
            value={form.mrn_prefix}
            onChange={(e) => setForm({ ...form, mrn_prefix: e.target.value.toUpperCase() })}
            pattern="^[A-Z0-9_-]{1,16}$"
            maxLength={16}
            title="1-16 chars: A-Z, 0-9, dash or underscore."
            placeholder="e.g. MRN, CITY-1"
            data-testid="h-prefix"
            className="mt-1 w-full px-3 py-1.5 rounded-sm border border-[var(--hms-border-2)] bg-white text-sm font-mono"
          />
        </div>
        <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="h-phone" />
        <Field label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="h-email" />
        <Field label="Registration No." value={form.registration_no} onChange={(v) => setForm({ ...form, registration_no: v })} testid="h-reg" />
        <Field label="NABH No." value={form.nabh_no} onChange={(v) => setForm({ ...form, nabh_no: v })} testid="h-nabh" />
        <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="h-address" colSpan={2} />
        <div className="col-span-2 flex items-center gap-3 pt-2 border-t border-dashed border-[var(--hms-border)]">
          <input
            id="h-opd-toggle"
            type="checkbox"
            checked={!!form.opd_module_enabled}
            onChange={(e) => setForm({ ...form, opd_module_enabled: e.target.checked })}
            data-testid="h-opd-enabled"
            className="h-4 w-4 rounded-sm border-[var(--hms-border-2)] accent-[var(--hms-accent)] cursor-pointer"
          />
          <label htmlFor="h-opd-toggle" className="text-sm font-medium cursor-pointer">
            Enable <span className="font-semibold">OPD module</span> for this hospital
          </label>
          <span className="text-[11px] text-[var(--hms-muted)] ml-auto">
            Disable if this hospital operates IPD-only.
          </span>
        </div>
        <DialogFooter className="col-span-2 mt-2">
          <Button type="submit" disabled={busy} data-testid={isEdit ? "h-update" : "h-submit"} className="rounded-sm bg-[var(--hms-sidebar)] hover:bg-[var(--hms-ink-2)]">
            {busy ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save changes" : "Create hospital")}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function Field({ label, value, onChange, required, testid, colSpan, disabled }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <Label className="text-[11px] uppercase tracking-wider font-bold text-[var(--hms-text-2)]">
        {label}{required && " *"}{disabled && <span className="ml-1 normal-case font-normal text-[var(--hms-muted)]">(immutable)</span>}
      </Label>
      <Input
        value={value} onChange={(e) => onChange(e.target.value)} required={required}
        disabled={disabled}
        data-testid={testid}
        className="mt-1 rounded-sm border-[var(--hms-border-2)] bg-white"
      />
    </div>
  );
}
